/**
 * liveTurnCardHtml — the parts of a LIVE-TURN CARD that are html: what someone in a
 * shared thread has Prest0n working on right now (§11 WS-2 / §12 PR-F2 of
 * docs/PLAN-mid-turn-fold-in-20260915.md; T-R13, Miles 2026-09-20, and his
 * 2026-09-21 directive on the card itself).
 *
 * Kept beside branchLane.ts and OUT of widgetHtml.ts on purpose: widgetHtml.ts is a
 * dependency-free leaf many pure tests import, and this module composes the rail
 * and the progress label from their own modules. It imports nothing that reaches
 * firebase — names are resolved in the host and arrive here as plain strings.
 *
 * WHO the card is about, in words (his ask, verbatim: "when Prest0n it responds and
 * it looks like 'M - you' (myself miles) is responding not Prest0n. It should be
 * clear 'Prest0n is responding to [Miles]'"): the headline is a SENTENCE naming the
 * asker, never the asker's chip alone. The glyph beside it is still theirs — it is
 * their ask the work belongs to.
 *
 * The contract, one card per running turn inside the ONE overlay lane:
 *  - a header row: the asker's glyph, `Prest0n is responding to <name>`, a spinner
 *    (one per turn, so several turns are several spinners), then the primary's own
 *    stage words with the step number and the elapsed clock
 *    (`<stage> · step N · m:ss`);
 *  - the branch rail under it when the primary parallelised — the SAME rows the own
 *    pill draws (branchLane.ts), spinners and all;
 *  - the STEPS RAIL is React, not html (LiveTurnStepsRail.tsx): it rotates as steps
 *    arrive and its disclosure opens the whole run, which a static string cannot do;
 *  - NEVER a stop button: `pending-widget-stop-btn` is the own pill's id and its
 *    click cancels THIS tab's thread — another person's work is not this tab's to
 *    stop. tests/groupThreads/liveTurnCardHtml.test.ts pins the count at zero;
 *  - NEVER a role, a permission fact or anyone's standing (R8): the header takes a
 *    name and a glyph and renders exactly those;
 *  - the words code authors here are STATE vocabulary only (D9: working · putting it
 *    together · step N · +N more); every other word on the card is the primary's own;
 *  - text on the card's light shimmer ground is `--brand-gold-text` (theme.ts: gold is a
 *    FILL, never text on a light ground — the own pill's `--brand-gold-hover` text is a
 *    baselined legacy defect, not a precedent; scripts/check-gold-as-text.mjs).
 */
import { buildBranchRailHtml } from './branchLane';
import { buildServerBackedProgressLabel } from '../prest0nSession/progress';
import {
  LIVE_TURN_RAIL_VISIBLE_MAX,
  type LiveTurnCard,
  type LiveTurnState,
} from '../groupThreads/liveTurns';

/** The R1 tag every card wears (`data-source-tag`); registry entry: config/code-authored-copy.d/group_live_turn.json. */
export const GROUP_LIVE_TURN_SOURCE_TAG = 'group_live_turn';
/** How many earlier steps a card lists under its current stage — the rail's own number. */
export const LIVE_TURN_STEPS_TAIL_MAX = LIVE_TURN_RAIL_VISIBLE_MAX;

export interface LiveTurnCardHeader {
  /** The asker's display name ("You" for the viewer) — a NAME, never a role. */
  name: string;
  /** The avatar glyph (senderGlyph of the underlying name). */
  initial: string;
}

const FONT = "font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;";

export function escapeLiveTurnHtml(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** The code-authored state vocabulary (D9 state). */
export function liveTurnStateWord(state: LiveTurnState): string {
  return state === 'converging' ? 'putting it together' : 'working';
}

/**
 * `Prest0n is responding to <name>` — and `… to you` when the asker is the viewer,
 * on their own devices as much as on anyone else's. A NAME only; R8 forbids the
 * card carrying a role, and the host resolves the name through the same roster
 * chain the sender chip reads.
 */
export function liveTurnHeadline(
  card: Pick<LiveTurnCard, 'self'>,
  header: Pick<LiveTurnCardHeader, 'name'>,
): string {
  const name = (header.name || '').trim();
  const who = card.self || !name || name.toLowerCase() === 'you' ? 'you' : name;
  return `Prest0n is responding to ${who}`;
}

/**
 * `<stage> · step N · m:ss` for a running turn; `putting it together · m:ss` while
 * converging. The step number is the run's TRUE count (the server's `stepCount`),
 * so a forty-seven-step run says forty-seven — the 2026-09-21 finding.
 */
export function liveTurnStageLine(
  card: Pick<LiveTurnCard, 'state' | 'stageLabel' | 'startedAtMs' | 'stepCount'>,
  nowMs: number,
): string {
  const converging = card.state === 'converging';
  const base = converging ? liveTurnStateWord(card.state) : (card.stageLabel || liveTurnStateWord(card.state));
  const count = Math.floor(card.stepCount ?? 0);
  const label = !converging && count > 0 ? `${base} · step ${count}` : base;
  return buildServerBackedProgressLabel(label, Math.max(0, nowMs - card.startedAtMs));
}

function avatarHtml(header: LiveTurnCardHeader, sizePx: number): string {
  const glyph = escapeLiveTurnHtml((header.initial || header.name || '?').trim().slice(0, 2) || '?');
  return (
    `<span class="pending-live-turn-avatar" aria-hidden="true" style="width:${sizePx}px;height:${sizePx}px;border-radius:50%;background:var(--brand-gold);color:#fff;font-size:${Math.max(9, Math.round(sizePx / 2))}px;font-weight:600;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;line-height:1;">${glyph}</span>`
  );
}

/**
 * The card's head: glyph, headline, spinner, stage line. The steps rail and the card
 * chrome are the component's (LiveTurnCardsOverlay.tsx) — a disclosure that opens
 * forty-seven rows cannot live in a string that repaints every second.
 */
export function buildLiveTurnCardHeadHtml(card: LiveTurnCard, header: LiveTurnCardHeader, nowMs: number): string {
  const stage = escapeLiveTurnHtml(liveTurnStageLine(card, nowMs));
  const headline = escapeLiveTurnHtml(liveTurnHeadline(card, header));
  return (
    `<div class="pending-live-turn-head" style="display:flex;align-items:center;gap:10px;min-width:0;max-width:100%;">` +
    avatarHtml(header, 22) +
    `<span class="pending-live-turn-name" style="font-weight:600;white-space:nowrap;flex-shrink:1;min-width:0;max-width:52%;">${headline}</span>` +
    `<span class="pending-spinner pending-live-turn-spinner" aria-hidden="true" style="width:14px;height:14px;border:2px solid rgba(198,167,94,0.2);border-top-color:var(--brand-gold);border-radius:50%;animation:pendingSpin 0.8s linear infinite;flex-shrink:0;"></span>` +
    `<span class="pending-live-turn-stage" style="flex:1;min-width:0;white-space:nowrap;">${stage}</span>` +
    '</div>'
  );
}

/**
 * The LANES: one row per parallel branch, the same rows the own pill draws, plus the
 * convergence line the moment they are all back (Miles, 2026-09-21: "a way to display
 * when parallelization happens"). '' when the primary did not parallelise.
 */
export function buildLiveTurnLanesHtml(card: LiveTurnCard, nowMs: number): string {
  return buildBranchRailHtml(card.branches, nowMs);
}

/**
 * The header drawn ABOVE the viewer's OWN pill in a shared room — the same sentence
 * the cards carry, so the room and the asker read the work the same way. The pill
 * itself is untouched: its html, its stop button and its shape detection stay exactly
 * what they are.
 */
export function buildLiveTurnOwnHeaderHtml(
  header: LiveTurnCardHeader,
  card?: Pick<LiveTurnCard, 'stepCount'> | null,
): string {
  const headline = liveTurnHeadline({ self: true }, header);
  // P-2 (Miles, 2026-09-21 — "there was actually 47 steps and not a hand full"):
  // the ROOM's card reads `<stage> · step N · m:ss`; the asker's pill reads
  // `<stage> · m:ss` and had no step at all, so the one person who asked was the
  // one person who could not see how much work their question had caused. The
  // count is the server's own `stepCount` off the live doc — the SAME number the
  // room reads, not the planner-iteration number on the task document, which is a
  // different quantity and would have shown the asker a different figure.
  //
  // It goes in the HEADER rather than into the pill's label because the pill's html
  // is a string built in another module (its stop-button id and shape detection are
  // pinned), and because the pill directly below already carries the stage and the
  // clock — repeating them here would be noise. Same facts, one line up.
  const count = Math.floor(card?.stepCount ?? 0);
  const step = count > 0 ? ` · step ${count}` : '';
  return (
    `<div class="pending-live-turn-own-head" data-source-tag="${GROUP_LIVE_TURN_SOURCE_TAG}" style="display:flex;align-items:center;gap:8px;min-width:0;max-width:100%;padding:0 0 2px 6px;${FONT}font-size:12.5px;font-weight:600;color:var(--brand-gold-text);">` +
    avatarHtml(header, 18) +
    `<span class="pending-live-turn-name" style="white-space:nowrap;min-width:0;">${escapeLiveTurnHtml(`${headline}${step}`)}</span>` +
    '</div>'
  );
}
